import React from 'react';
import { StarRatingQuestion } from '../StarRatingQuestion';
import { MultipleChoiceQuestion } from '../MultipleChoiceQuestion';
import { MatrixQuestion } from '../MatrixQuestion';
import { MultiLineTextQuestion } from '../MultiLineTextQuestion';
import { NumberRatingQuestion } from '../NumberRatingQuestion';
import { EmailQuestion } from '../EmailQuestion';
import { SingleLineTextQuestion } from '../SingleLineTextQuestion';
import { DropdownQuestion } from '../DropdownQuestion';
import type { QuestionRendererProps } from '../SurveyPage.types';

/**
 * QuestionRenderer Component
 *
 * A factory component that renders the appropriate question component based on
 * the question type. This component encapsulates the switch logic for determining
 * which specific question component to render.
 *
 * Supported question types:
 * - star_rating: StarRatingQuestion component
 * - multiple_choice: MultipleChoiceQuestion component
 * - matrix: MatrixQuestion component
 * - multi_line_text: MultiLineTextQuestion component
 * - number_rating: NumberRatingQuestion component
 * - single_line_text: SingleLineTextQuestion component
 * - dropdown: DropdownQuestion component
 *
 * @param props - Component props including question data, value, and handlers
 * @returns The appropriate question component for the given question type
 *
 * @example
 * <QuestionRenderer
 *   question={question}
 *   value={responses[question.question_number]}
 *   onChange={(value) => updateResponse(question.question_number, value)}
 *   language="en"
 *   error={errors[question.question_number]}
 *   displayNumber={1}
 * />
 */
export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  value,
  onChange,
  error,
  displayNumber,
}) => {
  // Common props shared by all question components
  const commonProps = {
    error,
    displayNumber,
  } as const;

  // Wrapper function to add data attribute for scrolling to errors
  const renderWithWrapper = (questionComponent: React.ReactNode) => (
    <div data-question-number={question.question_number}>
      {questionComponent}
    </div>
  );

  switch (question.question_type) {
    case 'star_rating':
      return renderWithWrapper(
        <StarRatingQuestion
          key={question.question_number}
          question={question}
          value={typeof value === 'number' ? (value as number) : null}
          onChange={onChange}
          {...commonProps}
        />
      );

    case 'multiple_choice':
      return renderWithWrapper(
        <MultipleChoiceQuestion
          key={question.question_number}
          question={question}
          value={(value as string[]) || []}
          onChange={onChange}
          {...commonProps}
        />
      );

    case 'matrix':
      return renderWithWrapper(
        <MatrixQuestion
          key={question.question_number}
          question={question}
          value={(value as Record<number, number>) || {}}
          onChange={onChange}
          {...commonProps}
        />
      );

    case 'multi_line_text':
      return renderWithWrapper(
        <MultiLineTextQuestion
          key={question.question_number}
          question={question}
          value={(value as string) || ''}
          onChange={onChange}
          {...commonProps}
        />
      );

    case 'number_rating':
      return renderWithWrapper(
        <NumberRatingQuestion
          key={question.question_number}
          question={question}
          value={typeof value === 'number' ? (value as number) : null}
          onChange={onChange}
          {...commonProps}
        />
      );

    case 'single_line_text':
      return renderWithWrapper(
        <SingleLineTextQuestion
          key={question.question_number}
          question={question}
          value={(value as string) || ''}
          onChange={onChange}
          {...commonProps}
        />
      );

    case 'email':
      return renderWithWrapper(
        <EmailQuestion
          key={question.question_number}
          question={question}
          value={(value as string) || ''}
          onChange={onChange}
          {...commonProps}
        />
      );

    case 'dropdown':
      return renderWithWrapper(
        <DropdownQuestion
          key={question.question_number}
          question={question}
          value={(value as string) || ''}
          onChange={onChange}
          {...commonProps}
        />
      );

    default:
      {
        // Type guard to ensure exhaustive switch
        // If we reach here, a new question type was added but not handled
        const _exhaustiveCheck: never = question;
        console.warn('Unknown question type:', _exhaustiveCheck);
        return null;
      }
  }
};
